import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useInformarPagamentoEmLote } from "@/hooks/use-cargas";
import { fmtMoedaBR } from "@/lib/formato-mercado";
import { ErroApi } from "@/lib/queryClient";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import { FORMAS_LANCAMENTO, FORMA_LANCAMENTO_LABEL, type FormaLancamento } from "@shared/schema";
import type { LinhaListaPagarDTO } from "@shared/mercado-comprador";

/**
 * "Registrar pagamento" — UM documento ou VÁRIOS, o mesmo diálogo e a mesma rota.
 *
 * ⚠️ QUEM REGISTRA QUITA: cada documento fica PAGO para os dois lados na hora,
 * e o romaneio do Rizoma vira PAGA no Cacho do produtor. Não há "aguardando o
 * produtor" — e o texto não promete aviso, porque o app não manda mensagem.
 *
 * ⚠️ O lote é TUDO OU NADA no servidor (uma transação): se um documento for
 * recusado, nenhum é gravado, e a mensagem diz qual e por quê.
 *
 * O valor de cada linha entra com o SALDO (o caso de 9 em 10) e é editável; a
 * data entra com HOJE NA FAZENDA — `new Date().toISOString()` daria amanhã
 * depois das 21h em Irecê, e o servidor recusaria como "data no futuro".
 */

/**
 * "13.644,40" → 13644.4 — o que o corretor digita. `NaN` quando não é número.
 *
 * ⚠️ "7206.5" é 7.206,50, não 72.065: com vírgula, ponto é milhar; sem vírgula,
 * um ponto seguido de 1 ou 2 dígitos no fim é o decimal de quem digitou no
 * teclado numérico. Errar aqui é pagar dez vezes mais.
 */
export function valorDigitado(texto: string): number {
  let t = texto.trim().replace(/^R\$\s*/, "").replace(/\s/g, "");
  if (t.indexOf(",") >= 0) t = t.replace(/\./g, "").replace(",", ".");
  else if (!/^\d+\.\d{1,2}$/.test(t)) t = t.replace(/\./g, "");
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}

export function valorParaCampo(v: number): string {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** O que o diálogo lê de cada documento — o A pagar manda a linha inteira; o painel do romaneio, só isto. */
import { descricaoRegistrarPagamento } from "@shared/textos-pagamento";

export type DocumentoParaPagar = Pick<LinhaListaPagarDTO, "alvoId" | "natureza" | "documento" | "produtorNome" | "saldo">;

export function DialogPagamentoLote({
  linhas, onFechar, onPago,
}: {
  linhas: DocumentoParaPagar[] | null;
  onFechar: () => void;
  onPago?: () => void;
}) {
  const { toast } = useToast();
  const lote = useInformarPagamentoEmLote();
  const [valores, setValores] = useState<Record<string, string>>({});
  const [data, setData] = useState("");
  const [forma, setForma] = useState<FormaLancamento>("PIX");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  if (!linhas || linhas.length === 0) return null;

  const numeros = linhas.map(l => valorDigitado(valores[l.alvoId] ?? valorParaCampo(l.saldo)));
  const invalido = numeros.some(n => !(n > 0));
  // O total do BOTÃO é o que o corretor digitou, conferido antes de mandar; o
  // total que vale é o que o servidor devolve e grava.
  const total = numeros.reduce((a, n) => a + (n > 0 ? n : 0), 0);
  const umSo = linhas.length === 1;

  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent
        className="sm:max-w-lg max-h-[90svh] overflow-y-auto"
        onOpenAutoFocus={() => {
          setValores({});
          setData(diaCivilNaFazenda(new Date()));
          setForma("PIX");
          setObservacao("");
          setErro(null);
        }}
        data-testid="dialog-pagamento-lote"
      >
        <DialogHeader>
          <DialogTitle>{umSo ? "Registrar pagamento" : `Registrar pagamento de ${linhas.length} documentos`}</DialogTitle>
          <DialogDescription>
            {/* Pós-#246: no ROMANEIO o produtor não confirma — é avisado e, se o
                dinheiro não entrar, marca Não recebi. O texto sai de um derivador
                puro (shared/textos-pagamento.ts), que é o que o teste olha. */}
            {descricaoRegistrarPagamento(linhas)}
          </DialogDescription>
        </DialogHeader>

        <ul className="divide-y rounded-md border" data-testid="lista-lote">
          {linhas.map(l => (
            <li key={l.alvoId} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{l.documento} · {l.produtorNome}</p>
                <p className="text-xs text-muted-foreground">em aberto {fmtMoedaBR(l.saldo)}</p>
              </div>
              <Input
                aria-label={`Valor para ${l.documento} de ${l.produtorNome}`}
                inputMode="decimal"
                className="w-32 text-right font-mono tabular-nums"
                value={valores[l.alvoId] ?? valorParaCampo(l.saldo)}
                onChange={e => setValores({ ...valores, [l.alvoId]: e.target.value })}
                data-testid={`input-lote-valor-${l.alvoId}`}
              />
            </li>
          ))}
        </ul>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="lote-data">Data do pagamento</Label>
            <Input id="lote-data" type="date" value={data} onChange={e => setData(e.target.value)} data-testid="input-lote-data" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lote-forma">Forma</Label>
            <Select value={forma} onValueChange={v => setForma(v as FormaLancamento)}>
              <SelectTrigger id="lote-forma" data-testid="select-lote-forma"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMAS_LANCAMENTO.map(f => <SelectItem key={f} value={f}>{FORMA_LANCAMENTO_LABEL[f]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="-mt-1 text-xs text-muted-foreground">O dia em que o dinheiro saiu — não o de hoje, se foi antes.</p>

        <div className="space-y-1.5">
          <Label htmlFor="lote-obs">Observação <span className="font-normal text-muted-foreground">(opcional)</span></Label>
          <Textarea id="lote-obs" rows={2} maxLength={500} value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Ex.: PIX único para os três romaneios" data-testid="input-lote-obs" />
        </div>

        {erro && <p className="text-sm text-destructive" role="alert" data-testid="erro-lote">{erro}</p>}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            disabled={invalido || !data || lote.isPending}
            onClick={() => {
              setErro(null);
              lote.mutate(
                {
                  itens: linhas.map((l, i) => ({
                    natureza: l.natureza === "ROMANEIO" ? "ROMANEIO" : "REGISTRO",
                    alvoId: l.alvoId,
                    valor: Math.round(numeros[i] * 100) / 100,
                  })),
                  data,
                  forma,
                  observacao: observacao.trim() || null,
                },
                {
                  onSuccess: r => {
                    toast({
                      title: r.lancados === 1 ? "Pagamento registrado" : `${r.lancados} pagamentos registrados`,
                      description: fmtMoedaBR(r.total),
                    });
                    onPago?.();
                    onFechar();
                  },
                  // O servidor diz QUAL documento e por quê; nada foi gravado.
                  onError: (e: Error) => setErro(mensagemDoErro(e)),
                },
              );
            }}
            data-testid="button-registrar-lote"
          >
            {lote.isPending ? "Registrando…" : `Registrar pagamento · ${fmtMoedaBR(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** O `error` do servidor (qual documento e por quê), via `ErroApi`. */
export function mensagemDoErro(e: Error): string {
  const msg = e instanceof ErroApi && e.mensagem ? e.mensagem : e.message;
  return `${msg} Nada foi registrado.`;
}
